// DevFileTree.js — Arborescence lazy (un niveau à la fois)

import React, { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, File, Folder, Loader2, Files } from 'lucide-react';
import { getWorkspaceTree } from '../../services/devCockpitApi';
import './devCockpit.css';

function TreeNode({ entry, selectedPath, onSelect, depth, childCache, onLoadChildren, loadingPaths }) {
    const [open, setOpen] = useState(false);
    const isDir = entry.type === 'dir';
    const isSelected = entry.path === selectedPath;
    const isLoading = loadingPaths.has(entry.path);
    const children = childCache[entry.path] || entry.children || [];

    const handleClick = async () => {
        if (isDir) {
            const next = !open;
            setOpen(next);
            if (next && !childCache[entry.path] && entry.hasChildren !== false) {
                await onLoadChildren(entry.path);
            }
        } else {
            onSelect(entry.path);
        }
    };

    return (
        <div>
            <button
                type="button"
                onClick={handleClick}
                className={`dev-tree-node__btn${isSelected ? ' dev-tree-node__btn--selected' : ''}`}
                style={{ paddingLeft: 7 + depth * 13 }}
            >
                {isDir ? (
                    isLoading ? (
                        <Loader2 size={11} className="dev-tree-node__chevron" style={{ animation: 'spin 1s linear infinite' }} />
                    ) : open ? (
                        <ChevronDown size={11} className="dev-tree-node__chevron" />
                    ) : (
                        <ChevronRight size={11} className="dev-tree-node__chevron" />
                    )
                ) : (
                    <span className="dev-tree-node__chevron" />
                )}
                {isDir ? <Folder size={12} className="dev-tree-node__icon" /> : <File size={12} className="dev-tree-node__icon" />}
                <span className="dev-tree-node__name">{entry.name}</span>
            </button>
            {isDir && open && children.map((child) => (
                <TreeNode
                    key={child.path}
                    entry={child}
                    selectedPath={selectedPath}
                    onSelect={onSelect}
                    depth={depth + 1}
                    childCache={childCache}
                    onLoadChildren={onLoadChildren}
                    loadingPaths={loadingPaths}
                />
            ))}
        </div>
    );
}

export default function DevFileTree({ entries, selectedPath, onSelectFile, loading, truncated, total }) {
    const [childCache, setChildCache] = useState({});
    const [loadingPaths, setLoadingPaths] = useState(new Set());

    const onLoadChildren = useCallback(async (dirPath) => {
        let alreadyLoaded = false;
        setChildCache((prev) => {
            if (prev[dirPath]) alreadyLoaded = true;
            return prev;
        });
        if (alreadyLoaded) return;

        setLoadingPaths((prev) => new Set(prev).add(dirPath));
        try {
            const data = await getWorkspaceTree(dirPath, true);
            setChildCache((prev) => ({ ...prev, [dirPath]: data.entries || [] }));
        } catch {
            setChildCache((prev) => ({ ...prev, [dirPath]: [] }));
        } finally {
            setLoadingPaths((prev) => {
                const next = new Set(prev);
                next.delete(dirPath);
                return next;
            });
        }
    }, []);

    return (
        <div className="dev-tree-panel">
            <div className="dev-frame__panel-header">
                <span>Explorateur</span>
                {!loading && entries?.length > 0 && (
                    <span className="dev-frame__panel-header-meta">
                        {truncated ? `${entries.length}/${total}` : entries.length}
                    </span>
                )}
            </div>

            {truncated && (
                <div className="dev-tree-panel__warn">
                    Liste tronquée — ouvrez un sous-dossier.
                </div>
            )}

            <div className="dev-tree-panel__scroll">
                {loading && (
                    <div className="dev-tree-panel__empty">
                        <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', marginBottom: 6 }} />
                        Chargement…
                    </div>
                )}
                {!loading && !entries?.length && (
                    <div className="dev-tree-panel__empty">
                        <Files size={14} style={{ marginBottom: 6, opacity: 0.5 }} />
                        Aucun fichier
                    </div>
                )}
                {!loading && entries?.map((entry) => (
                    <TreeNode
                        key={entry.path}
                        entry={entry}
                        selectedPath={selectedPath}
                        onSelect={onSelectFile}
                        depth={0}
                        childCache={childCache}
                        onLoadChildren={onLoadChildren}
                        loadingPaths={loadingPaths}
                    />
                ))}
            </div>
        </div>
    );
}
